import { useEffect } from "react";

import { useGroupLanding, useProjectRouteTarget } from "~/hooks/useProjectRouteTarget";
import { resolveLandingNavigation } from "~/projectRouteTarget";
import { useProjectGroupSettings } from "~/hooks/useProjectGroupSettings";
import { ProjectBoardPicker } from "./ProjectBoardPicker";
import { ProjectGroupSettings } from "./ProjectGroupSettings";
import { ProjectGroupEmptyState } from "./ProjectGroupEmptyState";

export interface ProjectGroupLandingProps {
  readonly projectKey: string;
  readonly onNavigate: (navigation: {
    readonly to: "/project/$projectKey" | "/project/$projectKey/tasks/$cmmdProjectId";
    readonly params: { readonly projectKey: string; readonly cmmdProjectId?: string };
  }) => void;
  readonly onLeave: () => void;
  /** The requested tab, if any. `settings` belongs to the GROUP, not a board. */
  readonly tab?: string | undefined;
}

/**
 * Turns a resolved route target into exactly one outcome, and renders nothing
 * of its own while the answer is still `pending`.
 *
 * Navigation happens in an effect rather than during render because a redirect
 * issued mid-render is a side effect in a pure function — React may run it
 * twice, and the second run would fight the first.
 */
export function ProjectGroupLanding(props: ProjectGroupLandingProps) {
  const target = useProjectRouteTarget(props.projectKey);
  const groupKey = target.kind === "render" ? target.groupKey : null;
  const landing = useGroupLanding(groupKey);
  const { onNavigate, onLeave } = props;

  const navigation = resolveLandingNavigation(target, landing, props.tab);

  useEffect(() => {
    // Navigation in an effect rather than during render: a redirect issued
    // mid-render is a side effect in a pure function, and React may run it
    // twice with the second run fighting the first.
    if (navigation.kind === "leave") onLeave();
    if (navigation.kind === "navigate") onNavigate(navigation);
  }, [navigation, onNavigate, onLeave]);

  if (target.kind !== "render") return null;
  // Settings outrank the board rules: a repo with exactly one board is the
  // common case, and falling through would make its own settings unreachable.
  if (props.tab === "settings") {
    return <GroupSettings groupKey={target.groupKey} onLeave={props.onLeave} />;
  }
  if (landing.kind === "choose") {
    return (
      <ProjectBoardPicker groupKey={target.groupKey} cmmdProjectIds={landing.cmmdProjectIds} />
    );
  }
  if (landing.kind === "empty") return <ProjectGroupEmptyState groupKey={target.groupKey} />;
  // `board` is handled by the effect above; rendering nothing avoids a flash of
  // the wrong surface before the navigation lands.
  return null;
}

/**
 * Supplies the settings page from the store.
 *
 * A separate component because the hook must not run for a group that is still
 * resolving — calling it above the `render` guard would read members for a key
 * that has no group yet.
 */
function GroupSettings(props: { readonly groupKey: string; readonly onLeave: () => void }) {
  const state = useProjectGroupSettings(props.groupKey);
  return (
    <ProjectGroupSettings groupKey={props.groupKey} {...state} onRemovalComplete={props.onLeave} />
  );
}
